/**
 * Utility functions for the Resultatrapport (Income Statement).
 *
 * Contains:
 * - K2-compliant income statement structure definition
 * - Number formatting (Swedish locale)
 * - PDF export
 * - K2 subtotal definitions
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ---------------------------------------------------------------------------
// K2 Income Statement Structure
// Matches backend basAccountPlan.js INCOME_STATEMENT_STRUCTURE
// Per BFNAR 2016:10 p 4.4
// ---------------------------------------------------------------------------

export const MAIN_SECTIONS = [
    'Rörelseintäkter, lagerförändringar m.m.',
    'Rörelsekostnader',
    'Finansiella poster',
    'Bokslutsdispositioner',
    'Skatter',
];

/**
 * K2-required subtotal lines, inserted BETWEEN main sections.
 * Each subtotal appears after the named section.
 */
export const K2_SUBTOTALS = [
    {
        key: 'rorelseresultat',
        label: 'Rörelseresultat',
        afterSection: 'Rörelsekostnader',
    },
    {
        key: 'resultat_efter_finansiella_poster',
        label: 'Resultat efter finansiella poster',
        afterSection: 'Finansiella poster',
    },
    {
        key: 'resultat_fore_skatt',
        label: 'Resultat före skatt',
        afterSection: 'Bokslutsdispositioner',
    },
    {
        key: 'arets_resultat',
        label: 'Årets resultat',
        afterSection: 'Skatter',
    },
];

/**
 * Working-report-only extra subtotals (NOT part of K2 ÅR).
 * Shown by Fortnox, Visma etc in daily reports.
 *
 * Each entry specifies:
 * - afterSection: the main section after which to insert this subtotal
 * - computeForYear(yearData): function that computes the subtotal from year data
 */
export const WORKING_SUBTOTALS = [
    {
        key: 'bruttovinst',
        label: 'Bruttovinst',
        afterSection: 'Rörelseintäkter, lagerförändringar m.m.',
        computeForYear: (yearData) => {
            // Revenue - Cost of goods (Råvaror + Handelsvaror)
            const revenue = yearData?.['Rörelseintäkter, lagerförändringar m.m.']?.total_change || 0;
            const ravaror = yearData?.['Rörelsekostnader']?.['Råvaror och förnödenheter']?.total_change || 0;
            const handelsvaror = yearData?.['Rörelsekostnader']?.['Handelsvaror']?.total_change || 0;
            return parseFloat(revenue) + parseFloat(ravaror) + parseFloat(handelsvaror);
        },
    },
];

/**
 * Standard income statement structure for frontend account assignment.
 * Account ranges match backend basAccountPlan.js exactly.
 */
export function getStandardIncomeStatementStructure() {
    return {
        'Rörelseintäkter, lagerförändringar m.m.': {
            'Nettoomsättning': {
                accounts: [],
                total_change: 0,
                accountRanges: ['30', '31'],
            },
            'Förändring av lager av produkter i arbete, färdiga varor och pågående arbete för annans räkning': {
                accounts: [],
                total_change: 0,
                accountRanges: ['32', '33', '34'],
            },
            'Aktiverat arbete för egen räkning': {
                accounts: [],
                total_change: 0,
                accountRanges: ['38'],
            },
            'Övriga rörelseintäkter': {
                accounts: [],
                total_change: 0,
                accountRanges: ['35', '36', '37', '39'],
            },
            total_change: 0,
        },
        'Rörelsekostnader': {
            'Råvaror och förnödenheter': {
                accounts: [],
                total_change: 0,
                accountRanges: ['41', '42', '43', '44', '45', '46', '47', '48', '49'],
            },
            'Handelsvaror': {
                accounts: [],
                total_change: 0,
                accountRanges: ['40'],
            },
            'Övriga externa kostnader': {
                accounts: [],
                total_change: 0,
                accountRanges: [
                    '50', '51', '52', '53', '54', '55', '56', '57', '58', '59',
                    '60', '61', '62', '63', '64', '65', '66', '67', '68', '69',
                ],
            },
            'Personalkostnader': {
                'Löner och andra ersättningar': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['70', '71', '72'],
                    workingReportOnly: true,
                },
                'Kostnadsersättningar och naturaförmåner': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['73'],
                    workingReportOnly: true,
                },
                'Pensionskostnader': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['74'],
                    workingReportOnly: true,
                },
                'Sociala avgifter': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['75'],
                    workingReportOnly: true,
                },
                'Övriga personalkostnader': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['76'],
                    workingReportOnly: true,
                },
                accounts: [],
                total_change: 0,
                // Parent-level ranges for K2 view (single "Personalkostnader" line)
                accountRanges: ['70', '71', '72', '73', '74', '75', '76'],
            },
            'Av- och nedskrivningar av materiella och immateriella anläggningstillgångar': {
                accounts: [],
                total_change: 0,
                accountRanges: ['77', '78'],
            },
            'Nedskrivningar av omsättningstillgångar utöver normala nedskrivningar': {
                accounts: [],
                total_change: 0,
                accountRanges: [], // Rarely used — K2 required post
            },
            'Övriga rörelsekostnader': {
                accounts: [],
                total_change: 0,
                accountRanges: ['79'],
            },
            total_change: 0,
        },
        'Finansiella poster': {
            'Resultat från andelar i koncernföretag': {
                accounts: [],
                total_change: 0,
                accountRanges: ['80'],
            },
            'Resultat från andelar i intresseföretag och gemensamt styrda företag': {
                accounts: [],
                total_change: 0,
                accountRanges: ['81'],
            },
            'Resultat från företag som det finns ett ägarintresse i': {
                accounts: [],
                total_change: 0,
                accountRanges: ['820', '821', '822', '823', '824'],
            },
            'Resultat från övriga finansiella anläggningstillgångar': {
                accounts: [],
                total_change: 0,
                accountRanges: ['825', '826', '827', '828', '829'],
            },
            'Övriga ränteintäkter och liknande resultatposter': {
                accounts: [],
                total_change: 0,
                accountRanges: ['83'],
            },
            'Nedskrivningar av finansiella anläggningstillgångar och kortfristiga placeringar': {
                accounts: [],
                total_change: 0,
                accountRanges: ['85'],
            },
            'Räntekostnader och liknande resultatposter': {
                accounts: [],
                total_change: 0,
                accountRanges: ['84', '86'],
            },
            total_change: 0,
        },
        'Bokslutsdispositioner': {
            'Erhållna koncernbidrag': {
                accounts: [],
                total_change: 0,
                accountRanges: ['871'],
            },
            'Lämnade koncernbidrag': {
                accounts: [],
                total_change: 0,
                accountRanges: ['872'],
            },
            'Förändring av periodiseringsfonder': {
                accounts: [],
                total_change: 0,
                accountRanges: ['880', '881', '882', '883', '884'],
            },
            'Förändring av överavskrivningar': {
                accounts: [],
                total_change: 0,
                accountRanges: ['885', '886'],
            },
            'Övriga bokslutsdispositioner': {
                accounts: [],
                total_change: 0,
                accountRanges: ['870', '873', '874', '875', '876', '877', '878', '879', '887', '888', '889'],
            },
            total_change: 0,
        },
        'Skatter': {
            'Skatt på årets resultat': {
                accounts: [],
                total_change: 0,
                accountRanges: ['890', '891', '892', '893', '894', '895', '896', '897', '898'],
            },
            'Övriga skatter': {
                accounts: [],
                total_change: 0,
                accountRanges: ['899'],
            },
            total_change: 0,
        },
    };
}

// ---------------------------------------------------------------------------
// Section display title mapping
// ---------------------------------------------------------------------------

/**
 * Section display titles for the working report view.
 * For formal K2 ÅR, use the MAIN_SECTIONS names directly (per BFNAR 2016:10 p 3.4).
 */
export const SECTION_DISPLAY_TITLES = {
    'Rörelseintäkter, lagerförändringar m.m.': 'Rörelseintäkter, lagerförändringar m.m.',
    'Rörelsekostnader': 'Rörelsekostnader',
    'Finansiella poster': 'Finansiella poster',
    'Bokslutsdispositioner': 'Bokslutsdispositioner',
    'Skatter': 'Skatter',
};

// ---------------------------------------------------------------------------
// Section path traversal (shared between table and PDF export)
// ---------------------------------------------------------------------------

// Section names that contain dots — must be handled specially in path splitting
const SECTIONS_WITH_DOTS = ['Rörelseintäkter, lagerförändringar m.m.'];

/**
 * Smart-split a dot-separated path, handling section names that contain dots.
 */
function smartSplitPath(path) {
    for (const name of SECTIONS_WITH_DOTS) {
        if (path.startsWith(name + '.')) {
            return [name, ...path.substring(name.length + 1).split('.')];
        }
        if (path === name) {
            return [name];
        }
    }
    return path.split('.');
}

/**
 * Navigate into a year's data by dot-separated path.
 * Handles section names containing dots (e.g., "Rörelseintäkter, lagerförändringar m.m.").
 *
 * @param {object} data - Year data object
 * @param {string} path - Dot-separated path
 * @returns {object|null} The section at that path, or null
 */
export function getSectionByPath(data, path) {
    if (!data) return null;
    if (data[path]) return data[path];

    const parts = smartSplitPath(path);
    let current = data;
    for (const part of parts) {
        if (!current || !current[part]) return null;
        current = current[part];
    }
    return current;
}

/**
 * Check if a year's data has any activity.
 */
export function yearHasData(yearStruct) {
    if (!yearStruct) return false;
    return MAIN_SECTIONS.some(k => {
        const sec = yearStruct[k];
        if (!sec) return false;
        return Math.abs(parseFloat(sec.total_change || 0)) > 0.01;
    });
}

// ---------------------------------------------------------------------------
// Number Formatting (Swedish locale)
// ---------------------------------------------------------------------------

/**
 * Format amount for display. Negative shown in parentheses.
 * Returns empty string for zero/null/undefined.
 */
export function formatAmount(amount) {
    if (amount === undefined || amount === null || amount === '') return '';
    const numAmount = parseFloat(String(amount).replace(/,/g, ''));
    if (isNaN(numAmount)) return '';
    if (Math.abs(numAmount) < 0.005) return '';

    return new Intl.NumberFormat('sv-SE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }).format(numAmount);
}

/**
 * Format amount, always showing a value (0,00 if empty).
 */
export function formatAmountShowZero(amount) {
    if (amount === undefined || amount === null) return '0,00';
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('sv-SE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }).format(numAmount);
}

// ---------------------------------------------------------------------------
// PDF Export
// ---------------------------------------------------------------------------

/**
 * Export income statement to PDF using jspdf-autotable.
 */
export function exportResultReportToPDF(processedData, subtotals) {
    if (!processedData) return;

    const doc = new jsPDF();
    const availableYears = processedData.years || [];

    // Header
    doc.setFontSize(14);
    doc.text('RESULTATRÄKNING', 14, 15);
    doc.setFontSize(10);
    doc.text(`Jämförelse: ${availableYears.join(', ')}`, 14, 22);

    // Build table columns
    const columns = ['', ...availableYears.map(String)];

    // Build table rows from processed data
    const rows = [];
    const mainSections = MAIN_SECTIONS;

    for (const sectionName of mainSections) {
        // Section header row
        rows.push([{
            content: SECTION_DISPLAY_TITLES[sectionName] || sectionName,
            colSpan: columns.length,
            styles: { fontStyle: 'bold', fillColor: [248, 249, 250] },
        }]);

        // Get section data from first year with data
        const displayYear = availableYears[0];
        const yearData = processedData.data[displayYear];
        if (!yearData || !yearData[sectionName]) continue;

        const sectionData = yearData[sectionName];

        // Subsections and accounts
        _addSectionRowsToPDF(rows, sectionData, sectionName, processedData, availableYears, 0);

        // Section total row
        const totalRow = [{ content: `Summa ${sectionName.toLowerCase()}`, styles: { fontStyle: 'bold' } }];
        availableYears.forEach(year => {
            const yd = processedData.data[year];
            const total = yd?.[sectionName]?.total_change || 0;
            totalRow.push({ content: formatAmountShowZero(total), styles: { fontStyle: 'bold', halign: 'right' } });
        });
        rows.push(totalRow);

        // K2 subtotal after this section
        const subtotal = K2_SUBTOTALS.find(s => s.afterSection === sectionName);
        if (subtotal && subtotals) {
            const subtotalRow = [{
                content: subtotal.label.toUpperCase(),
                styles: { fontStyle: 'bold', fillColor: [233, 236, 239] },
            }];
            availableYears.forEach(year => {
                const yearSubtotals = subtotals[year];
                const value = yearSubtotals?.[subtotal.key] || 0;
                subtotalRow.push({
                    content: formatAmountShowZero(value),
                    styles: { fontStyle: 'bold', halign: 'right', fillColor: [233, 236, 239] },
                });
            });
            rows.push(subtotalRow);
        }
    }

    // Generate table
    doc.autoTable({
        head: [columns],
        body: rows,
        startY: 28,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [52, 58, 64] },
        columnStyles: {
            0: { cellWidth: 80 },
        },
    });

    doc.save(`Resultatrapport_${availableYears.join('_')}.pdf`);
}

function _addSectionRowsToPDF(rows, sectionData, sectionPath, processedData, availableYears, depth) {
    if (!sectionData || typeof sectionData !== 'object') return;

    Object.entries(sectionData).forEach(([key, value]) => {
        if (key === 'total_change' || key === 'accountRanges' || key === 'accounts' || key === 'workingReportOnly') return;
        if (typeof value !== 'object') return;
        // Skip workingReportOnly subsections in PDF (K2 ÅR format per BFNAR 2016:10 p 3.4)
        if (value.workingReportOnly) return;

        // Subsection header
        const indent = '  '.repeat(depth);
        const row = [{ content: `${indent}${key}`, styles: { fontStyle: 'bold' } }];
        availableYears.forEach(year => {
            const yearData = processedData.data[year];
            const section = _getSectionByPath(yearData, sectionPath, key);
            const total = section?.total_change || 0;
            row.push({ content: formatAmountShowZero(total), styles: { fontStyle: 'bold', halign: 'right' } });
        });
        rows.push(row);

        // Accounts in this subsection
        if (value.accounts && value.accounts.length > 0) {
            value.accounts.forEach(account => {
                const accRow = [`${indent}  ${account.account_number} ${account.account_name}`];
                availableYears.forEach(year => {
                    const yearData = processedData.data[year];
                    const section = _getSectionByPath(yearData, sectionPath, key);
                    const accInYear = section?.accounts?.find(a =>
                        a.account_id === account.account_id || a.account_number === account.account_number
                    );
                    const amount = accInYear ? parseFloat(String(accInYear.change || 0).replace(/,/g, '')) : 0;
                    accRow.push({ content: formatAmount(amount), styles: { halign: 'right' } });
                });
                rows.push(accRow);
            });
        }

        // Recurse into deeper subsections
        _addSectionRowsToPDF(rows, value, `${sectionPath}.${key}`, processedData, availableYears, depth + 1);
    });
}

function _getSectionByPath(yearData, sectionPath, subsectionKey) {
    const section = getSectionByPath(yearData, sectionPath);
    if (!section) return null;
    return subsectionKey ? section[subsectionKey] : section;
}
